import { createContext, useContext, useEffect, useMemo } from "react";
import { useNetworkErrorDialog } from "./NetworkErrorDialog";
import React from "react";
import { useGenericErrorDialog } from "./GenericErrorDialog";
import { isNetworkError } from "@tlc/ls2pac/components/AxiosInstanceManager";

export interface ErrorDialogOptions {
  hint?: string; // optional custom text for technical details
  onConfirm?: () => void; // optionally override the default window reload 
  confirmButtonText?: string;
}


type ErrorDialogContextType = {
  handleRequestError: (error: unknown, options?: ErrorDialogOptions) => void;
};
export const ErrorDialogContext = createContext<ErrorDialogContextType | null>(null);

export function useErrorDialog() {
  const context = useContext(ErrorDialogContext);

  if (!context) {
    throw new Error(
      "useErrorDialog must be used within an ErrorDialogProvider",
    );
  }

  return context;
}

export const ErrorDialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { openNetworkErrorDialog, networkErrorDialog } =
    useNetworkErrorDialog();
  const { openGenericErrorDialog, genericErrorDialog } =
    useGenericErrorDialog();

  const handleRequestError = React.useCallback(
    (error: unknown, options?: ErrorDialogOptions) => {
      console.error(error);
      if (isNetworkError(error)) {
        openNetworkErrorDialog(error);
      } else {
        openGenericErrorDialog(error, options);
      }
    },
    [openGenericErrorDialog, openNetworkErrorDialog],
  );

  const contextValue = React.useMemo(
    () => ({
      handleRequestError,
    }),
    [openGenericErrorDialog, openNetworkErrorDialog, handleRequestError],
  );

  return (
    <ErrorDialogContext.Provider value={contextValue}>
      {children}
      {networkErrorDialog}
      {genericErrorDialog}
    </ErrorDialogContext.Provider>
  );
};
